import { ENVELOPE_SOURCE, ENVELOPE_VERSION, WIDGET_BASE_URL } from "./constants"
import { findBootScript, maybeAutoboot } from "./autoboot"
import { createParentBridge, type ParentBridge } from "./bridge"
import { createWidgetFrame, removeWidgetFrame } from "./iframe"
import { detectPageSku, readPathname } from "./page-context"
import { createPageContextPoster } from "./page-context-poster"
import { watchSoftNavigation } from "./soft-navigation"
import type {
  CloseResult,
  InitOptions,
  PageContextParams,
  StartParams,
  VinciWidgetInstance,
} from "./types"

export * from "./types"

export const VinciWidgetSDK = {
  init(options?: InitOptions): VinciWidgetInstance {
    if (!WIDGET_BASE_URL || WIDGET_BASE_URL === "undefined") {
      throw new Error(
        "[VinciWidgetSDK] WIDGET_BASE_URL was not injected at build time. " +
          "Use the prebuilt dist bundle, not the SDK source.",
      )
    }

    let iframe: HTMLIFrameElement | null = null
    let container: HTMLDivElement | null = null
    let bridge: ParentBridge | null = null

    const pageContext = createPageContextPoster({
      detectSku: () =>
        detectPageSku(
          typeof document === "undefined" ? null : document,
          findBootScript(
            typeof document === "undefined" ? null : document.currentScript,
          ),
        ),
      readPath: () => readPathname(window.location.href),
      post: (context) => {
        iframe?.contentWindow?.postMessage(
          {
            source: ENVELOPE_SOURCE,
            version: ENVELOPE_VERSION,
            event: "page_context",
            payload: context,
          },
          WIDGET_BASE_URL,
        )
      },
      watch: (detect, post) => watchSoftNavigation(detect, post),
    })

    const instance: VinciWidgetInstance = {
      start({ userToken, partnerSlug, conversationId }: StartParams) {
        if (iframe) {
          console.warn("[VinciWidgetSDK] Already started. Call close() first.")
          return
        }
        if (!userToken || !partnerSlug || !conversationId) {
          console.error(
            "[VinciWidgetSDK] start() requires userToken, partnerSlug, and conversationId",
          )
          return
        }

        const sessionId = conversationId
        const parentOrigin = window.location.origin
        // The token is intentionally NOT in the URL (leaks via history,
        // referrers, and logs). It is delivered over postMessage once the
        // widget signals it is ready.
        const url =
          `${WIDGET_BASE_URL}/vinci-widget/${sessionId}` +
          `?p=${encodeURIComponent(partnerSlug)}` +
          `&o=${encodeURIComponent(parentOrigin)}`

        const result = createWidgetFrame(url, options?.containerId)
        iframe = result.iframe
        container = result.container
        bridge = createParentBridge(iframe, {
          onWidgetReady: () => {
            // targetOrigin is pinned to the widget origin — never "*".
            iframe?.contentWindow?.postMessage(
              {
                source: ENVELOPE_SOURCE,
                version: ENVELOPE_VERSION,
                event: "auth_token",
                payload: { token: userToken },
              },
              WIDGET_BASE_URL,
            )
          },
          onPageContextEnabled: () => pageContext.enable(),
        })
      },

      close(): CloseResult {
        bridge?.dispose()
        pageContext.reset()
        if (iframe && container) removeWidgetFrame(iframe, container)
        iframe = null
        container = null
        bridge = null
        return { closed: true }
      },

      setContext({ sku }: PageContextParams) {
        pageContext.setOverride(sku)
      },

      on(event, handler) {
        if (!bridge) {
          console.warn(
            "[VinciWidgetSDK] .on() called before .start(); handler will not fire.",
          )
          return () => {}
        }
        return bridge.on(event, handler)
      },
    }

    return instance
  },
}

export default VinciWidgetSDK

// One-tag embed: boots the floating launcher only when the loading <script>
// tag carries data-partner-slug. No-op otherwise.
if (typeof document !== "undefined") {
  maybeAutoboot(VinciWidgetSDK)
}
